import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';
import {EyebrowPill} from '../components/EyebrowPill';
import {InputModeToggle} from '../components/app/InputModeToggle';
import {CreateTimerButton} from '../components/app/CreateTimerButton';
import {PhoneFrame} from '../components/app/PhoneFrame';

export type PasteToTimerProps = {
  eyebrow: string;
  pastedWorkoutText: string;
};

// 10s = 300 frames @ 30fps
const TEXTFIELD_IN = 25;
const CURSOR_TO_FIELD = 60;
const FOCUS_AT = 88;
const TYPE_START = 100;
const TYPE_END = 200;
const CURSOR_TO_BTN = 210;
const TAP = 232;
const DIAL_FADE_OUT = 270;
const DIAL_FADE_IN = 282;

export const PasteToTimer: React.FC<PasteToTimerProps> = ({eyebrow, pastedWorkoutText}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const headerOpacity = interpolate(frame, [0, 14], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const phoneSpring = spring({
    frame: Math.max(0, frame - 4),
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 90},
  });
  const phoneY = interpolate(phoneSpring, [0, 1], [60, 0]);
  const phoneOpacity = interpolate(frame, [4, 22], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill>
      <BgGradient />
      <Audio src={staticFile('music/photo-to-timer.mp3')} volume={0.4} />

      <AbsoluteFill
        style={{
          padding: '90px 60px 110px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 30,
        }}
      >
        <div style={{opacity: headerOpacity}}>
          <EyebrowPill label={eyebrow} />
        </div>

        <div
          style={{
            flex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: phoneOpacity,
            transform: `translateY(${phoneY}px)`,
          }}
        >
          <PhoneFrame width={620} height={1280}>
            <Interior frame={frame} fps={fps} pastedWorkoutText={pastedWorkoutText} />
          </PhoneFrame>
        </div>

        <Caption frame={frame} />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const Interior: React.FC<{
  frame: number;
  fps: number;
  pastedWorkoutText: string;
}> = ({frame, fps, pastedWorkoutText}) => {
  const dialOpacity = interpolate(
    frame,
    [DIAL_FADE_IN, DIAL_FADE_IN + 14],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const dialScale = spring({
    frame: Math.max(0, frame - DIAL_FADE_IN),
    fps,
    from: 0.86,
    to: 1,
    config: {damping: 12, stiffness: 90},
  });
  const dialVisible = frame >= DIAL_FADE_IN - 4;

  const inputOpacity = interpolate(
    frame,
    [DIAL_FADE_OUT, DIAL_FADE_IN],
    [1, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );

  return (
    <div style={{position: 'absolute', inset: 0}}>
      {frame < DIAL_FADE_IN ? (
        <InputPane
          frame={frame}
          fps={fps}
          opacity={inputOpacity}
          pastedWorkoutText={pastedWorkoutText}
        />
      ) : null}

      {dialVisible ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: dialOpacity,
            transform: `scale(${dialScale})`,
            transformOrigin: 'center',
          }}
        >
          <Img
            src={staticFile('images/app-timer-custom-ready.png')}
            style={{width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center'}}
          />
        </div>
      ) : null}
    </div>
  );
};

const InputPane: React.FC<{
  frame: number;
  fps: number;
  opacity: number;
  pastedWorkoutText: string;
}> = ({frame, fps, opacity, pastedWorkoutText}) => {
  const fieldSpring = spring({
    frame: Math.max(0, frame - TEXTFIELD_IN),
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 100},
  });
  const fieldOpacity = interpolate(frame, [TEXTFIELD_IN, TEXTFIELD_IN + 14], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const fieldY = interpolate(fieldSpring, [0, 1], [24, 0]);

  const focusProgress = interpolate(frame, [FOCUS_AT, FOCUS_AT + 8], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const words = pastedWorkoutText.split(/(\s+)/);
  const totalReveal = Math.max(0, words.length);
  const revealProgress = interpolate(frame, [TYPE_START, TYPE_END], [0, totalReveal], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const revealedCount = Math.floor(revealProgress);
  const hasTyped = frame >= TYPE_START;

  const isCreating = frame >= TAP && frame < DIAL_FADE_OUT;

  // Cursor: idle off-screen → field → create button
  const cursorX = (() => {
    if (frame < FOCUS_AT) {
      return interpolate(frame, [CURSOR_TO_FIELD, FOCUS_AT], [400, 200], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
    }
    if (frame < CURSOR_TO_BTN) return 200;
    return interpolate(frame, [CURSOR_TO_BTN, TAP], [200, 300], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
  })();
  const cursorY = (() => {
    if (frame < FOCUS_AT) {
      return interpolate(frame, [CURSOR_TO_FIELD, FOCUS_AT], [320, 350], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
    }
    if (frame < CURSOR_TO_BTN) return 350;
    return interpolate(frame, [CURSOR_TO_BTN, TAP], [350, 620], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
  })();
  const cursorOpacity = interpolate(
    frame,
    [TEXTFIELD_IN + 4, TEXTFIELD_IN + 14, TAP + 6, TAP + 14],
    [0, 1, 1, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const tapPulse = interpolate(frame, [TAP - 4, TAP, TAP + 10], [0, 1.7, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        padding: '110px 28px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
        opacity,
      }}
    >
      <InputModeToggle selected="write" />

      <div
        style={{
          opacity: fieldOpacity,
          transform: `translateY(${fieldY}px)`,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <PasteTextField
          frame={frame}
          words={words}
          revealedCount={revealedCount}
          hasTyped={hasTyped}
          focusProgress={focusProgress}
        />
        <CreateTimerButton isCreating={isCreating} frame={frame} />
      </div>

      <TouchCursor x={cursorX} y={cursorY} opacity={cursorOpacity} pulse={tapPulse} />
    </div>
  );
};

const PasteTextField: React.FC<{
  frame: number;
  words: string[];
  revealedCount: number;
  hasTyped: boolean;
  focusProgress: number;
}> = ({frame, words, revealedCount, hasTyped, focusProgress}) => {
  const borderColor = `rgba(139,92,246,${focusProgress})`;
  const baseBorder = `rgba(255,255,255,${0.06 * (1 - focusProgress)})`;
  const cursorBlink = Math.floor(frame / 14) % 2 === 0 ? 1 : 0;
  const text = words.slice(0, revealedCount).join('');

  return (
    <div
      style={{
        minHeight: 200,
        padding: '20px 22px',
        background: 'rgba(38,38,38,0.9)',
        borderRadius: 22,
        border: `2px solid ${baseBorder}`,
        boxShadow: focusProgress > 0 ? `0 0 0 2px ${borderColor}` : undefined,
        fontFamily: brandFont,
        fontSize: 24,
        lineHeight: 1.5,
        whiteSpace: 'pre-line',
        position: 'relative',
        color: 'rgba(255,255,255,0.94)',
      }}
    >
      {hasTyped ? (
        <>
          {text}
          <span style={{opacity: cursorBlink, color: brand.primary, fontWeight: 400}}>|</span>
        </>
      ) : (
        <span style={{color: 'rgba(255,255,255,0.34)'}}>
          Paste or describe a workout to create a timer
        </span>
      )}
    </div>
  );
};

const TouchCursor: React.FC<{x: number; y: number; opacity: number; pulse: number}> = ({
  x,
  y,
  opacity,
  pulse,
}) => (
  <div
    style={{
      position: 'absolute',
      left: x,
      top: y,
      width: 44,
      height: 44,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.92)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
      opacity,
      pointerEvents: 'none',
      transform: 'translate(-50%, -50%)',
    }}
  >
    {pulse > 0 ? (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: `3px solid ${brand.primary}`,
          opacity: 1 - pulse / 1.7,
          transform: `scale(${pulse})`,
        }}
      />
    ) : null}
  </div>
);

const Caption: React.FC<{frame: number}> = ({frame}) => {
  const opacity = interpolate(frame, [30, 54], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const label = (() => {
    if (frame < TYPE_START) return 'Just paste it.';
    if (frame < TAP) return 'Tap Create timer.';
    if (frame < DIAL_FADE_IN) return 'AI reads it.';
    return 'Timer ready.';
  })();

  return (
    <div
      style={{
        opacity,
        fontFamily: brandFont,
        fontSize: 40,
        fontWeight: 700,
        color: brand.textPrimary,
        letterSpacing: -0.4,
        textAlign: 'center',
        minHeight: 56,
      }}
    >
      {label}
    </div>
  );
};
